import { createContext, useState, useEffect, useContext } from "react";
import apiClient from "../api/apiClient";
import { API_ENDPOINTS } from "../constants/apiEndpoints";
import { toast } from "react-hot-toast";
import { useSelector } from "react-redux";

// Create Cart Context
export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const isLoggedIn = useSelector((state) => state.auth.user);

  const [cart, setCart] = useState({});
  const [cartItems, setCartItems] = useState([]);
  const [cartLength, setCartLength] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Coupon state
  const [couponCode, setCouponCode] = useState(() => {
    return localStorage.getItem("appliedCoupon") || "";
  });
  
  const [discountPercent, setDiscountPercent] = useState(() => {
    const savedDiscount = localStorage.getItem("discountPercent");
    return savedDiscount ? parseInt(savedDiscount) : 0;
  });
  
  const [couponApplied, setCouponApplied] = useState(() => {
    return localStorage.getItem("appliedCoupon") ? true : false;
  });

  // Available coupons - could be fetched from API in a production app
  const availableCoupons = {
    WELCOME10: 10, // 10% discount
    SUMMER20: 20, // 20% discount
    SPECIAL25: 25, // 25% discount
  };

  // Save coupon info to localStorage whenever it changes
  useEffect(() => {
    if (couponApplied && couponCode) {
      localStorage.setItem("appliedCoupon", couponCode);
      localStorage.setItem("discountPercent", discountPercent.toString());
    } else {
      localStorage.removeItem("appliedCoupon");
      localStorage.removeItem("discountPercent");
    }
  }, [couponApplied, couponCode, discountPercent]);

  // Fetch Cart (only if user is logged in)
  const fetchCart = async () => {
    if (!isLoggedIn) return;

    setLoading(true);
    try {
      const response = await apiClient.get(API_ENDPOINTS.GET_CART);
      const fetchedCart = response.data?.items || response.data?.cart || [];

      const formattedCartItems = fetchedCart.map((item) => ({
        productId: item.productId?._id || item.product?.id || item._id,
        quantity: item.quantity || 1,
        totalPrice: item.totalPrice || item.price * (item.quantity || 1),
        productName: item.product?.name || item.name,
        productUnitPrice: item.product?.unitPrice || item.price || item.unitPrice,
        productImage: item.product?.image || item.image,
        alt: item.product?.name || item.name,
      }));

      const cartMap = {};
      fetchedCart.forEach((item) => {
        const productId = item.productId?._id || item.product?.id || item._id;
        cartMap[productId] = item.quantity || 1;
      });

      setCart(cartMap);
      setCartItems(formattedCartItems);

      const totalQuantity = formattedCartItems.reduce((acc, item) => acc + item.quantity, 0);
      setCartLength(totalQuantity);

      setLoading(false);
    } catch (err) {
      console.error("Error fetching cart:", err);
      setError("Failed to load cart");
      setLoading(false);
      // toast.error("Error fetching cart");
    }
  };

  // Add item to cart
  const addToCart = async (productId) => {
    if (!isLoggedIn) {
      toast.error("Please log in to add items to the cart");
      return;
    }

    try {
      await apiClient.post(API_ENDPOINTS.ADD_TO_CART, { productId, quantity: 1 });
      fetchCart();
    } catch (err) {
      console.error("Error adding to cart:", err);
      toast.error("Error adding item to cart");
    }
  };

  // Remove item from cart
  const removeFromCart = async (productId) => {
    if (!isLoggedIn) {
      toast.error("Please log in to remove items from the cart");
      return;
    }

    try {
      await apiClient.delete(API_ENDPOINTS.REMOVE_FROM_CART, {
        data: { productId },
      });
      fetchCart();
    } catch (err) {
      console.error("Error removing from cart:", err);
      toast.error("Error removing item from cart");
    }
  };

  // Update cart quantity
  const updateQuantity = async (productId, quantity) => {
    if (!isLoggedIn) {
      toast.error("Please log in to update the cart");
      return;
    }

    try {
      if (quantity <= 0) {
        await removeFromCart(productId);
      } else {
        await apiClient.put(API_ENDPOINTS.UPDATE_CART, { productId, quantity });
        fetchCart();
      }
    } catch (err) {
      console.error("Error updating cart:", err);
      toast.error("Error updating cart");
    }
  };

  // Total quantity helper
  const getTotalQuantity = () => {
    return cartItems.reduce((acc, item) => acc + item.quantity, 0);
  };
  
  // Function to apply coupon
  const applyCoupon = (code) => {
    const upperCaseCoupon = code.toUpperCase();
    
    if (availableCoupons[upperCaseCoupon]) {
      setCouponCode(upperCaseCoupon);
      setDiscountPercent(availableCoupons[upperCaseCoupon]);
      setCouponApplied(true);
      return { success: true, message: "Coupon applied successfully!" };
    } else {
      return { success: false, message: "Invalid coupon code" };
    }
  };
  
  // Function to remove coupon
  const removeCoupon = () => {
    setCouponCode("");
    setDiscountPercent(0);
    setCouponApplied(false);
  };
  
  // Fixed shipping charge
const shippingCharge = 99;

  // Calculate original subtotal (before discount)
  const calculateSubtotal = () => {
    return cartItems.reduce((sum, item) => {
      const price = item.productUnitPrice || 0;
      return sum + price * item.quantity;
    }, 0);
  };
  
  // Calculate discount amount
  const calculateDiscount = () => {
    const subtotal = calculateSubtotal();
    return couponApplied ? (subtotal * discountPercent) / 100 : 0;
  };

  // Calculate total price

const grandCalculateTotalPrice = () => {
  const subtotal = calculateSubtotal();
  const discount = calculateDiscount();
  return subtotal - discount + shippingCharge;
};

  // Fetch cart on login change
  useEffect(() => {
    if (isLoggedIn) {
      fetchCart();
    } else {
      setCart({});
      setCartItems([]);
      setCartLength(0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  return (
    <CartContext.Provider
      value={{
        cart,
        cartLength,
        addToCart,
        removeFromCart,
        updateQuantity,
        loading,
        error,
        getTotalQuantity,
        cartItems,
        fetchCart,
        couponCode,
        discountPercent,
        couponApplied,
        applyCoupon,
        removeCoupon,
        calculateSubtotal,
        calculateDiscount,
        availableCoupons,
        grandCalculateTotalPrice
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);